import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { api } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import Footer from "../components/Footer";
import { toast } from "sonner";
import { errMsg } from "../lib/errors";
import { ShieldCheck, LogOut } from "lucide-react";

export default function AcceptBaa() {
  const { user, refresh, logout } = useAuth();
  const nav = useNavigate();
  const [checked, setChecked] = useState(false);
  const [loading, setLoading] = useState(false);

  // Distinguish first-time consent from version-update re-consent.
  const isVersionUpdate = !!user?.baa_accepted_at;
  const oldVersion = user?.baa_accepted_version;
  const newVersion = user?.baa_current_version;

  const accept = async () => {
    if (!checked) {
      toast.error("Please check the agreement box to continue");
      return;
    }
    setLoading(true);
    try {
      await api.post("/auth/accept-baa", { baa_accepted: true });
      await refresh();
      toast.success("Agreement recorded — welcome back");
      nav("/dashboard", { replace: true });
    } catch (err) {
      toast.error(errMsg(err, "Could not record acceptance"));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div
      className="min-h-screen flex flex-col"
      style={{ background: "var(--pa-bg)" }}
      data-testid="accept-baa-page"
    >
      <header className="px-6 lg:px-12 py-6 border-b border-[var(--pa-border)] shrink-0 flex items-center justify-between">
        <Link to="/" className="flex items-baseline gap-2">
          <span className="font-serif text-2xl text-[var(--pa-text)]">Prostho</span>
          <span className="font-serif text-2xl text-[var(--pa-gold)]">AI</span>
        </Link>
        <button
          onClick={() => {
            logout();
            nav("/", { replace: true });
          }}
          data-testid="accept-baa-signout"
          className="flex items-center gap-1.5 text-xs text-[var(--pa-text-muted)] hover:text-[var(--pa-gold)] transition"
        >
          <LogOut size={14} /> Sign out
        </button>
      </header>

      <main className="flex-1 px-6 py-12">
        <div className="max-w-2xl mx-auto">
          <div className="flex items-center gap-3 kicker">
            <ShieldCheck size={14} className="text-[var(--pa-gold)]" />
            {isVersionUpdate ? "Agreement updated" : "One-time consent"}
          </div>
          <h1
            className="font-serif text-3xl md:text-4xl text-[var(--pa-text)] mt-2"
            data-testid="accept-baa-title"
          >
            {isVersionUpdate
              ? "Our HIPAA agreement has been updated"
              : "Updated agreement for handling Protected Health Information"}
          </h1>
          <p className="mt-4 text-sm text-[var(--pa-text-muted)] leading-relaxed">
            Hi {user?.full_name?.split(" ")[0] || "Doctor"} —{" "}
            {isVersionUpdate ? (
              <>
                we've revised our Business Associate Agreement and Terms of
                Service. To continue using ProsthoAI, please review the
                updated documents and re-confirm your acceptance. Your
                previous acceptance ({oldVersion || "older version"}) is
                preserved in your audit history; this confirmation records
                the new version ({newVersion}).
              </>
            ) : (
              <>
                ProsthoAI now requires every clinician on the platform to
                accept our Business Associate Agreement and Terms of Service
                before continuing to use the Service. Your existing account
                was created before this requirement, so we need a one-time
                confirmation from you.
              </>
            )}
          </p>
          <p className="mt-3 text-xs text-[var(--pa-text-dim)] leading-relaxed">
            Your acceptance is recorded with a timestamp, IP address, and
            version identifier. You can review the full text of both documents
            via the links below.
          </p>

          <div className="gold-divider my-8" />

          <label
            htmlFor="accept-baa-checkbox"
            data-testid="accept-baa-label"
            className={`flex items-start gap-3 p-4 rounded-md border cursor-pointer transition ${
              checked
                ? "border-[var(--pa-gold)] bg-[var(--pa-gold)]/5"
                : "border-[var(--pa-border)] hover:border-[var(--pa-gold)]/60"
            }`}
          >
            <input
              id="accept-baa-checkbox"
              type="checkbox"
              checked={checked}
              onChange={(e) => setChecked(e.target.checked)}
              data-testid="accept-baa-checkbox"
              className="mt-0.5 accent-[var(--pa-gold)] shrink-0"
            />
            <span className="text-sm text-[var(--pa-text)] leading-relaxed">
              I agree to the ProsthoAI{" "}
              <Link
                to="/baa"
                target="_blank"
                rel="noopener"
                data-testid="accept-baa-baa-link"
                className="text-[var(--pa-gold)] hover:underline underline-offset-2"
              >
                Business Associate Agreement
              </Link>{" "}
              and{" "}
              <Link
                to="/terms"
                target="_blank"
                rel="noopener"
                data-testid="accept-baa-terms-link"
                className="text-[var(--pa-gold)] hover:underline underline-offset-2"
              >
                Terms of Service
              </Link>{" "}
              governing the handling of Protected Health Information.
            </span>
          </label>

          <div className="mt-8 flex items-center gap-3">
            <button
              onClick={accept}
              disabled={!checked || loading}
              data-testid="accept-baa-submit"
              className="bg-[var(--pa-gold)] text-[var(--pa-bg)] font-semibold rounded-md px-6 py-3 hover:bg-[var(--pa-gold-hover)] transition disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {loading ? "Recording…" : "Agree & continue"}
            </button>
            <span className="text-xs text-[var(--pa-text-dim)]">
              Required to continue using ProsthoAI
            </span>
          </div>
        </div>
      </main>
      <Footer />
    </div>
  );
}
